import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Upload, X, Download, Trash2, Edit2, Search, Filter } from 'lucide-react';
import { Card, Input, Select, InputGroup, ConfirmationModal, toast } from '../../components/v2/V2Components';
import DocumentService from '../../services/DocumentService';
import HouseService from '../../services/HouseService';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

const DOCUMENT_TYPE_LABELS = {
    insurance: 'Insurance',
    deed: 'Deed',
    contract: 'Contract',
    manual: 'Manual',
    warranty: 'Warranty',
    receipt: 'Receipt',
    other: 'Other'
};

const Documents = () => {
    usePageView(ANALYTICS_EVENTS.DOCUMENTS_VIEWED);
    const [documents, setDocuments] = useState([]);
    const [houses, setHouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    // Upload form state
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadName, setUploadName] = useState('');
    const [uploadDescription, setUploadDescription] = useState('');
    const [uploadType, setUploadType] = useState('other');
    const [uploadHouseId, setUploadHouseId] = useState('');
    const [uploading, setUploading] = useState(false);

    // Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('');

    const fetchDocuments = useCallback(async () => {
        try {
            setLoading(true);
            const data = await DocumentService.getDocuments();
            setDocuments(data);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch documents:', err);
            setError('Failed to load documents');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchHouses = useCallback(async () => {
        try {
            const data = await HouseService.getHouses();
            setHouses(data);
        } catch (err) {
            console.error('Failed to fetch houses:', err);
        }
    }, []);

    useEffect(() => {
        fetchDocuments();
        fetchHouses();
    }, [fetchDocuments, fetchHouses]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                toast.error('Only PDF files are allowed');
                return;
            }
            if (file.size > 50 * 1024 * 1024) {
                toast.error('File size must be less than 50MB');
                return;
            }
            setUploadFile(file);
            if (!uploadName) {
                setUploadName(file.name.replace('.pdf', ''));
            }
        }
    };

    const handleUpload = async () => {
        if (!uploadFile) {
            toast.error('Please select a file');
            return;
        }

        setUploading(true);
        try {
            await DocumentService.uploadDocument(uploadFile, {
                name: uploadName || uploadFile.name,
                description: uploadDescription,
                document_type: uploadType,
                house_id: uploadHouseId || null
            });

            toast.success('Document uploaded successfully');
            setShowUploadModal(false);
            resetUploadForm();
            fetchDocuments();
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error(err.response?.data?.error || 'Failed to upload document');
        } finally {
            setUploading(false);
        }
    };

    const resetUploadForm = () => {
        setUploadFile(null);
        setUploadName('');
        setUploadDescription('');
        setUploadType('other');
        setUploadHouseId('');
    };

    const handleDownload = async (doc) => {
        try {
            const { url, filename } = await DocumentService.getDownloadUrl(doc.id);
            // Open in new tab or trigger download
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Download failed:', err);
            toast.error('Failed to download document');
        }
    };

    const handleEdit = (doc) => {
        setSelectedDocument(doc);
        setShowEditModal(true);
    };

    const handleEditSave = async (updates) => {
        try {
            await DocumentService.updateDocument(selectedDocument.id, updates);
            toast.success('Document updated');
            setShowEditModal(false);
            setSelectedDocument(null);
            fetchDocuments();
        } catch (err) {
            console.error('Update failed:', err);
            toast.error('Failed to update document');
        }
    };

    const handleDelete = (doc) => {
        setSelectedDocument(doc);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            await DocumentService.deleteDocument(selectedDocument.id);
            toast.success('Document deleted');
            fetchDocuments();
        } catch (err) {
            console.error('Delete failed:', err);
            toast.error('Failed to delete document');
        }
        setSelectedDocument(null);
    };

    // Filter documents
    const filteredDocuments = documents.filter(doc => {
        const matchesSearch = !searchQuery ||
            doc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            doc.original_filename?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesType = !filterType || doc.document_type === filterType;
        return matchesSearch && matchesType;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary)]"></div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--text-main)] theme-transition">Documents</h1>
                    <p className="text-[var(--text-secondary)] theme-transition">
                        Store and manage your home-related documents
                    </p>
                </div>
                <button
                    onClick={() => setShowUploadModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition"
                >
                    <Upload size={18} />
                    Upload Document
                </button>
            </div>

            {/* Filters */}
            <Card className="p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)]" />
                        <input
                            type="text"
                            placeholder="Search documents..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter size={18} className="text-[var(--text-muted)]" />
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="px-3 py-2 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none"
                        >
                            <option value="">All Types</option>
                            {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </Card>

            {/* Error State */}
            {error && (
                <Card className="p-6 text-center text-red-500 mb-6">
                    {error}
                </Card>
            )}

            {/* Empty State */}
            {!error && filteredDocuments.length === 0 && (
                <Card className="p-8 text-center">
                    <div className="w-16 h-16 bg-[var(--bg-hover)] rounded-full flex items-center justify-center mx-auto mb-6 theme-transition">
                        <FileText size={32} className="text-[var(--primary)]" />
                    </div>
                    <h2 className="text-xl font-bold text-[var(--text-main)] mb-2 theme-transition">
                        {searchQuery || filterType ? 'No matching documents' : 'No documents yet'}
                    </h2>
                    <p className="text-[var(--text-secondary)] mb-6 theme-transition">
                        {searchQuery || filterType
                            ? 'Try adjusting your search or filter'
                            : 'Upload your first document to get started'}
                    </p>
                    {!searchQuery && !filterType && (
                        <button
                            onClick={() => setShowUploadModal(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition"
                        >
                            <Upload size={18} />
                            Upload Document
                        </button>
                    )}
                </Card>
            )}

            {/* Document List */}
            {filteredDocuments.length > 0 && (
                <div className="grid gap-4">
                    {filteredDocuments.map((doc) => (
                        <Card key={doc.id} className="p-4">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FileText size={24} className="text-red-500" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-medium text-[var(--text-main)] truncate theme-transition">
                                        {doc.name}
                                    </h3>
                                    <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--text-secondary)] theme-transition">
                                        <span className="px-2 py-0.5 bg-[var(--bg-hover)] rounded text-xs">
                                            {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
                                        </span>
                                        <span>{DocumentService.formatFileSize(doc.file_size)}</span>
                                        <span>•</span>
                                        <span>{new Date(doc.upload_date).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleDownload(doc)}
                                        className="p-2 text-[var(--text-secondary)] hover:text-[var(--primary)] hover:bg-[var(--bg-hover)] rounded-lg transition"
                                        title="Download"
                                    >
                                        <Download size={18} />
                                    </button>
                                    <button
                                        onClick={() => handleEdit(doc)}
                                        className="p-2 text-[var(--text-secondary)] hover:text-[var(--primary)] hover:bg-[var(--bg-hover)] rounded-lg transition"
                                        title="Edit"
                                    >
                                        <Edit2 size={18} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(doc)}
                                        className="p-2 text-[var(--text-secondary)] hover:text-red-500 hover:bg-[var(--bg-hover)] rounded-lg transition"
                                        title="Delete"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                            {doc.description && (
                                <p className="mt-2 text-sm text-[var(--text-secondary)] theme-transition">
                                    {doc.description}
                                </p>
                            )}
                        </Card>
                    ))}
                </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="upload-document-modal-title"
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
                >
                    <div className="bg-[var(--bg-card)] w-full max-w-md rounded-2xl shadow-xl theme-transition">
                        <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
                            <h2 id="upload-document-modal-title" className="text-lg font-bold text-[var(--text-main)] theme-transition">Upload Document</h2>
                            <button
                                type="button"
                                aria-label="Close dialog"
                                onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                                className="p-1 text-[var(--text-muted)] hover:text-[var(--text-main)] transition"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-4 space-y-4">
                            {/* File Drop Zone */}
                            <div
                                className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                                    uploadFile
                                        ? 'border-[var(--primary)] bg-[var(--primary-bg)]'
                                        : 'border-[var(--border)] hover:border-[var(--primary)]'
                                }`}
                            >
                                <input
                                    type="file"
                                    accept=".pdf,application/pdf"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="file-upload"
                                />
                                <label htmlFor="file-upload" className="cursor-pointer">
                                    {uploadFile ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <FileText size={24} className="text-[var(--primary)]" />
                                            <span className="text-[var(--text-main)] font-medium">{uploadFile.name}</span>
                                        </div>
                                    ) : (
                                        <>
                                            <Upload size={32} className="mx-auto mb-2 text-[var(--text-muted)]" />
                                            <p className="text-[var(--text-main)] font-medium">Click to upload PDF</p>
                                            <p className="text-sm text-[var(--text-muted)]">Max file size: 50MB</p>
                                        </>
                                    )}
                                </label>
                            </div>

                            <InputGroup label="Document Name">
                                <Input
                                    value={uploadName}
                                    onChange={(e) => setUploadName(e.target.value)}
                                    placeholder="Enter document name"
                                />
                            </InputGroup>

                            <InputGroup label="Document Type">
                                <Select
                                    value={uploadType}
                                    onChange={(e) => setUploadType(e.target.value)}
                                >
                                    {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </Select>
                            </InputGroup>

                            {houses.length > 0 && (
                                <InputGroup label="Associate with House (Optional)">
                                    <Select
                                        value={uploadHouseId}
                                        onChange={(e) => setUploadHouseId(e.target.value)}
                                    >
                                        <option value="">No house selected</option>
                                        {houses.map((house) => (
                                            <option key={house.id} value={house.id}>{house.name}</option>
                                        ))}
                                    </Select>
                                </InputGroup>
                            )}

                            <InputGroup label="Description (Optional)">
                                <textarea
                                    value={uploadDescription}
                                    onChange={(e) => setUploadDescription(e.target.value)}
                                    placeholder="Add a description..."
                                    rows={3}
                                    className="w-full p-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none resize-none"
                                />
                            </InputGroup>
                        </div>

                        <div className="flex gap-3 p-4 border-t border-[var(--border)]">
                            <button
                                onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                                className="flex-1 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpload}
                                disabled={!uploadFile || uploading}
                                className="flex-1 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {uploading ? 'Uploading...' : 'Upload'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedDocument && (
                <EditDocumentModal
                    document={selectedDocument}
                    houses={houses}
                    onClose={() => { setShowEditModal(false); setSelectedDocument(null); }}
                    onSave={handleEditSave}
                />
            )}

            {/* Delete Confirmation */}
            <ConfirmationModal
                isOpen={showDeleteConfirm}
                onClose={() => { setShowDeleteConfirm(false); setSelectedDocument(null); }}
                onConfirm={confirmDelete}
                title="Delete Document"
                message={`Are you sure you want to delete "${selectedDocument?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                isDanger={true}
            />
        </div>
    );
};

// Edit Document Modal Component
const EditDocumentModal = ({ document, houses, onClose, onSave }) => {
    const [name, setName] = useState(document.name || '');
    const [description, setDescription] = useState(document.description || '');
    const [documentType, setDocumentType] = useState(document.document_type || 'other');
    const [houseId, setHouseId] = useState(document.house_id || '');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        await onSave({
            name,
            description,
            document_type: documentType,
            house_id: houseId || null
        });
        setSaving(false);
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-document-modal-title"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
        >
            <div className="bg-[var(--bg-card)] w-full max-w-md rounded-2xl shadow-xl theme-transition">
                <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
                    <h2 id="edit-document-modal-title" className="text-lg font-bold text-[var(--text-main)] theme-transition">Edit Document</h2>
                    <button
                        type="button"
                        aria-label="Close dialog"
                        onClick={onClose}
                        className="p-1 text-[var(--text-muted)] hover:text-[var(--text-main)] transition"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    <InputGroup label="Document Name">
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter document name"
                        />
                    </InputGroup>

                    <InputGroup label="Document Type">
                        <Select
                            value={documentType}
                            onChange={(e) => setDocumentType(e.target.value)}
                        >
                            {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </Select>
                    </InputGroup>

                    {houses.length > 0 && (
                        <InputGroup label="Associate with House">
                            <Select
                                value={houseId}
                                onChange={(e) => setHouseId(e.target.value)}
                            >
                                <option value="">No house selected</option>
                                {houses.map((house) => (
                                    <option key={house.id} value={house.id}>{house.name}</option>
                                ))}
                            </Select>
                        </InputGroup>
                    )}

                    <InputGroup label="Description">
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Add a description..."
                            rows={3}
                            className="w-full p-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none resize-none"
                        />
                    </InputGroup>
                </div>

                <div className="flex gap-3 p-4 border-t border-[var(--border)]">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex-1 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Documents;
